import {
  ApplicationVariantType,
  Criteria,
  MySoftwareCriteria,
  MySoftwareTableRow,
  PaginatedAPIResponse,
  SalesOrder,
  SelectionFilter,
  SortDirection,
} from '@order-management/types';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Element } from 'react-bulma-components';
import { FilterInputData } from '../../shared/filter-bar/filter-bar';
import useHttpRequest from '../../shared/hooks/http-request';
import DefaultInboxTable from '../../ui/inbox-table/default-inbox-table/default-inbox-table';
import InboxTable from '../../ui/inbox-table/inbox-table';
import { OnTableChangeData } from '../../ui/table/types';
import MySoftwareTable from '../my-software-table/my-software-table';
import SoftwareInbox from '../software-inbox/software-inbox';
import { fetchMySoftwareBySalesOrder as fetchMySoftwareBySalesOrderRequest } from '../software-service';

const FETCH_MY_SOFTWARE_SALES_ORDERS_ERROR_MESSAGE =
  'The software could not be fetched. Please try again';

const initialCriteria: Criteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: 'licensingProductGroup.name',
  sortDirection: SortDirection.ASC,
};

export type SoftwareInboxTableProps = {
  salesOrders: SalesOrder[];
  fetchMoreSalesOrders: () => void;
  hasMoreSalesOrders: boolean;
  mySoftwareCriteriaFilter: MySoftwareCriteria;
  loadingInitialData?: boolean;
  leftContent?: ReactNode;
  softwareVariantType: ApplicationVariantType;
  appliedFilters?: [string, FilterInputData<MySoftwareCriteria>][];
  enableSearch?: boolean;
  handleSearchChange?: (value: string) => void;
};

export function SoftwareInboxTable({
  salesOrders,
  fetchMoreSalesOrders,
  hasMoreSalesOrders,
  mySoftwareCriteriaFilter,
  loadingInitialData,
  leftContent,
  softwareVariantType,
  appliedFilters,
  enableSearch = true,
  handleSearchChange,
}: SoftwareInboxTableProps) {
  const latestDataFetchRef = useRef(0);
  const [selectedSalesOrderId, setSelectedSalesOrderId] = useState<string>();
  const [software, setSoftware] = useState<MySoftwareTableRow[]>([]);
  const [mySoftwareResponse, setMySoftwareResponse] =
    useState<PaginatedAPIResponse<MySoftwareTableRow>>();
  const [criteriaFilter, setCriteriaFilter] = useState<Criteria>({
    ...initialCriteria,
    search: mySoftwareCriteriaFilter.search,
  });
  const { executeHttpRequest } = useHttpRequest();
  const [loadingInitialSoftware, setLoadingInitialSoftware] = useState(false);

  const fetchSoftwareBySalesOrder = useCallback(
    (
      salesOrderId: string,
      criteria: MySoftwareCriteria,
      clearData?: boolean
    ) => {
      const currentRef = ++latestDataFetchRef.current;
      return executeHttpRequest({
        asyncFunction: async () => {
          const response = await fetchMySoftwareBySalesOrderRequest(
            salesOrderId,
            criteria
          );

          if (currentRef !== latestDataFetchRef.current) return;

          if (clearData) {
            setSoftware(response.data);
          } else {
            setSoftware((prevSoftware) => [...prevSoftware, ...response.data]);
          }
          setMySoftwareResponse(response);
        },
        customErrorMessage: FETCH_MY_SOFTWARE_SALES_ORDERS_ERROR_MESSAGE,
      });
    },
    [executeHttpRequest]
  );

  const fetchMoreSoftware = useCallback(async () => {
    const updatedCriteriaFilter = {
      ...criteriaFilter,
      pageNumber: criteriaFilter.pageNumber + 1,
    };

    if (selectedSalesOrderId) {
      await fetchSoftwareBySalesOrder(selectedSalesOrderId, {
        ...mySoftwareCriteriaFilter,
        ...updatedCriteriaFilter,
      });
      setCriteriaFilter(updatedCriteriaFilter);
    }
  }, [
    criteriaFilter,
    fetchSoftwareBySalesOrder,
    mySoftwareCriteriaFilter,
    selectedSalesOrderId,
  ]);

  const onSalesOrderSelect = useCallback(
    async (salesOrderId: string) => {
      const { itemsPerPage, pageNumber, sortBy, sortDirection } =
        initialCriteria;
      const updatedCriteria = {
        ...mySoftwareCriteriaFilter,
        itemsPerPage,
        pageNumber,
        sortBy,
        sortDirection,
      };
      setCriteriaFilter((prevCriteria) => ({
        ...initialCriteria,
        search: prevCriteria.search,
      }));
      setSelectedSalesOrderId(salesOrderId);

      setLoadingInitialSoftware(true);
      await fetchSoftwareBySalesOrder(salesOrderId, updatedCriteria, true);
      setLoadingInitialSoftware(false);
    },
    [fetchSoftwareBySalesOrder, mySoftwareCriteriaFilter]
  );

  const onSearchChange = useCallback(
    (value: string) => {
      if (!enableSearch) return;

      const updatedCriteria = { ...mySoftwareCriteriaFilter, search: value };
      const { pageNumber } = initialCriteria;
      setCriteriaFilter((prevCriteria) => ({
        ...prevCriteria,
        ...updatedCriteria,
        pageNumber,
      }));

      handleSearchChange?.(value);
    },
    [enableSearch, handleSearchChange, mySoftwareCriteriaFilter]
  );

  const onTableChange = useCallback(
    async (value: OnTableChangeData<MySoftwareTableRow>) => {
      const updatedCriteria = {
        ...value.config,
        sortBy: value.column?.key ?? '',
      };
      const { pageNumber } = initialCriteria;
      setCriteriaFilter((prevCriteria) => ({
        ...prevCriteria,
        ...updatedCriteria,
        pageNumber,
      }));
      setLoadingInitialSoftware(true);
      await fetchSoftwareBySalesOrder(
        selectedSalesOrderId!,
        {
          ...mySoftwareCriteriaFilter,
          ...updatedCriteria,
        },
        true
      );
      setLoadingInitialSoftware(false);
    },
    [fetchSoftwareBySalesOrder, mySoftwareCriteriaFilter, selectedSalesOrderId]
  );

  const tableConfig = useMemo(() => {
    const { search, sortBy, sortDirection } = criteriaFilter;
    return {
      search,
      sortBy,
      sortDirection,
    };
  }, [criteriaFilter]);

  const emptyContent = useMemo(() => {
    const emptyContentText =
      appliedFilters?.length || mySoftwareCriteriaFilter.search !== ''
        ? 'There are no software that match the selected criteria'
        : 'There are no software at the moment';
    return (
      <Element
        display="flex"
        justifyContent="center"
        alignItems="center"
        className="h-full"
      >
        <span className="has-text-weight-light">{emptyContentText}</span>
      </Element>
    );
  }, [appliedFilters?.length, mySoftwareCriteriaFilter.search]);

  const inbox = useMemo(
    () => (
      <SoftwareInbox
        fetchMore={fetchMoreSalesOrders}
        hasMore={hasMoreSalesOrders}
        onClick={onSalesOrderSelect}
        salesOrders={salesOrders}
        selectedSalesOrderId={selectedSalesOrderId}
        loadingInitialData={loadingInitialData}
      />
    ),
    [
      fetchMoreSalesOrders,
      hasMoreSalesOrders,
      loadingInitialData,
      onSalesOrderSelect,
      salesOrders,
      selectedSalesOrderId,
    ]
  );

  const table = useMemo(() => {
    if (selectedSalesOrderId) {
      return (
        <MySoftwareTable
          dataSource={software}
          fetchMore={fetchMoreSoftware}
          hasMore={!!mySoftwareResponse?.hasMore}
          loadingInitialData={loadingInitialSoftware}
          config={tableConfig}
          onChange={onTableChange}
          includeSalesOrderColumn={selectedSalesOrderId === SelectionFilter.ALL}
          softwareVariantType={softwareVariantType}
          empty={emptyContent}
        />
      );
    }
    return (
      <DefaultInboxTable text="Please select a sales order to display software" />
    );
  }, [
    emptyContent,
    fetchMoreSoftware,
    loadingInitialSoftware,
    mySoftwareResponse?.hasMore,
    onTableChange,
    selectedSalesOrderId,
    software,
    softwareVariantType,
    tableConfig,
  ]);

  useEffect(() => {
    const refetchSoftware = async () => {
      if (selectedSalesOrderId) {
        setLoadingInitialSoftware(true);
        await fetchSoftwareBySalesOrder(
          selectedSalesOrderId,
          {
            ...criteriaFilter,
            ...mySoftwareCriteriaFilter,
          },
          true
        );
        setLoadingInitialSoftware(false);
      }
    };
    refetchSoftware();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mySoftwareCriteriaFilter]);

  return (
    <InboxTable
      inbox={inbox}
      table={table}
      config={tableConfig}
      leftContent={leftContent}
      enableSearch={enableSearch}
      onSearchChange={onSearchChange}
    />
  );
}

export default SoftwareInboxTable;
